<script setup>
/**
 * Paste a signature block, get structured fields back, and say where they go.
 *
 * The everyday job this removes is retyping: an address out of an email, a
 * signature block from a supplier, a scrap someone wrote down on the phone.
 * The text goes to /api/parse/contact, which runs it through the low-tier
 * model and returns address + contact fields.
 *
 * One dialog handles the whole block - address, emails and phones together -
 * because that is how it arrives. Where each part lands is the caller's
 * business: it declares `targets`, the user ticks the ones they want, and the
 * choice comes back with the draft. An order routes the address to shipping,
 * billing or both; a customer or vendor takes an address record plus a contact
 * row per number.
 *
 * Nothing is saved here. The parse is shown as an editable form, the user
 * corrects whatever the model got wrong, and only then does the caller apply
 * it - so a bad parse costs a correction rather than bad data.
 */
import {computed, ref} from 'vue'
import helpers from '@/lib/helpers'
import FormAddress from '../form/form-address.vue'

const props = defineProps({
  // shown above the box - say what this particular caller will do with it
  intro: {type: String, default: ''},
  /**
   * Where the caller can put things. Each entry:
   *   {key, label, needs: 'address'|'email'|'phone', default?: bool, hint?}
   * A target whose `needs` the paste did not produce is hidden rather than
   * offered and then silently doing nothing.
   */
  targets: {type: Array, default: () => []},
})

const emit = defineEmits(['submit'])

// a real multi-line string - an &#10; entity inside a plain attribute is not
// decoded here and shows up verbatim in the box
const placeholder = `Jane Smith, Northwind Labs
123 Main St Suite 400
Springfield IL 62704
555-123-4567 · jane@northwindlabs.com`

const text = ref('')
const busy = ref(false)
const draft = ref(null)
const picked = ref({})          // target key -> bool

// which of the parsed emails / phones the user actually wants
const pickedEmails = ref([])
const pickedPhones = ref([])

const canParse = computed(() => text.value.trim().length > 0 && !busy.value)

const hasAddress = computed(() => {
  const d = draft.value
  return !!(d && (d.address1 || d.city || d.zip))
})

const hasAnything = computed(() => {
  const d = draft.value

  if (!d)
    return false

  return !!(d.name || d.company || hasAddress.value
      || (d.emails || []).length || (d.phones || []).length)
})

/** Targets worth offering - the paste actually produced what they consume. */
const liveTargets = computed(() => props.targets.filter((t) => {
  if (t.needs === 'address') return hasAddress.value
  if (t.needs === 'email') return pickedEmails.value.length > 0
  if (t.needs === 'phone') return pickedPhones.value.length > 0
  return true
}))

const nothingRouted = computed(() =>
    liveTargets.value.length > 0 && !liveTargets.value.some(t => picked.value[t.key]))

const parse = async () => {
  if (!canParse.value)
    return

  busy.value = true

  try {
    // no noload: the model takes a few seconds and the global spinner is the
    // only thing telling the user the click registered
    const res = await helpers.asyncAction('/api/parse/contact', 'POST', {
      data: {text: text.value},
    })

    draft.value = res
    pickedEmails.value = [...(res.emails || [])]
    pickedPhones.value = [...(res.phones || [])]

    // a target defaulting to true is only ticked when its data actually showed
    // up - no point pre-selecting "Order email" for a paste with no email
    const next = {}
    props.targets.forEach((t) => { next[t.key] = t.default !== false })
    picked.value = next

    if (!hasAnything.value)
      helpers.toast('Nothing recognisable in that text', true)
  } catch (e) {
    helpers.alert((e && e.message) || 'Could not parse that text')
  } finally {
    busy.value = false
  }
}

const apply = () => {
  if (!draft.value)
    return

  const routed = {}
  liveTargets.value.forEach((t) => { routed[t.key] = !!picked.value[t.key] })

  emit('submit', {
    ...draft.value,
    emails: pickedEmails.value,
    phones: pickedPhones.value,
    targets: routed,
  })
}

/** Start again without closing the dialog. */
const reset = () => {
  draft.value = null
  picked.value = {}
  pickedEmails.value = []
  pickedPhones.value = []
}
</script>

<template>
  <modal title="Paste Contact Details" large height="600px" use-max-height>
    <div class="mx-3 my-2">
      <p class="small text-muted">
        {{ intro || 'Paste an address, an email signature or a scribbled note. It is read and split into fields for you to check before anything is saved.' }}
      </p>

      <!-- STEP 1: the paste -->
      <template v-if="!draft">
        <b-form-textarea
            v-model="text"
            rows="10"
            autofocus
            :placeholder="placeholder"/>
        <b-form-text>
          Address, emails and phone numbers all at once — nothing is invented, and a
          field the text does not contain comes back blank.
        </b-form-text>
      </template>

      <!-- STEP 2: check, correct, and route -->
      <template v-else>
        <b-alert :show="!hasAnything" variant="warning" class="py-2 small">
          No address or contact details were recognised. Go back and check the text.
        </b-alert>

        <!-- where it all goes. First, because it is the decision the reviewer
             is actually making - the fields below are just confirmation. -->
        <form-card title="Apply To" v-if="liveTargets.length">
          <b-form-checkbox v-for="t in liveTargets" :key="t.key"
                           v-model="picked[t.key]" class="mb-1">
            {{ t.label }}
            <span class="small text-muted" v-if="t.hint">— {{ t.hint }}</span>
          </b-form-checkbox>

          <b-alert :show="nothingRouted" variant="warning" class="py-1 px-2 mt-2 mb-0 small">
            Nothing is ticked, so nothing will be applied.
          </b-alert>
        </form-card>

        <form-card title="Person and Company">
          <form-row label="Name">
            <b-form-input size="sm" v-model="draft.name"/>
          </form-row>
          <form-row label="Company">
            <b-form-input size="sm" v-model="draft.company"/>
          </form-row>
          <form-row label="Title" v-if="draft.title">
            <b-form-input size="sm" v-model="draft.title"/>
          </form-row>
        </form-card>

        <form-card title="Address">
          <form-address v-model="draft"/>
          <b-form-checkbox v-model="draft.residential" class="mt-2 small">
            Residential address
          </b-form-checkbox>
        </form-card>

        <form-card title="Email and Phone">
          <div v-if="!(draft.emails || []).length && !(draft.phones || []).length"
               class="text-muted small">
            No email or phone found.
          </div>

          <b-form-group v-if="(draft.emails || []).length" label="Emails"
                        label-class="small text-muted pb-0">
            <b-form-checkbox-group v-model="pickedEmails" :options="draft.emails" stacked/>
          </b-form-group>

          <b-form-group v-if="(draft.phones || []).length" label="Phones"
                        label-class="small text-muted pb-0" class="mb-0">
            <b-form-checkbox-group v-model="pickedPhones" :options="draft.phones" stacked/>
          </b-form-group>
        </form-card>

        <form-card title="Notes" v-if="draft.notes">
          <b-form-textarea size="sm" rows="2" v-model="draft.notes"/>
        </form-card>
      </template>
    </div>

    <template #footer>
      <!-- the modal footer is text-right, not flex, so spacing is explicit -->
      <b-button v-if="draft" size="sm" variant="outline-secondary" class="mr-2" @click="reset">
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-1"/>Paste Something Else
      </b-button>

      <b-button v-if="!draft" size="sm" variant="primary" class="px-4"
                :disabled="!canParse" @click="parse">
        <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" class="mr-1"/>Read It
      </b-button>

      <b-button v-else size="sm" variant="primary" class="px-4"
                :disabled="!hasAnything || nothingRouted" @click="apply">
        Use These Details
      </b-button>
    </template>
  </modal>
</template>
